<template>
  <div class="fade-in">
    <h2 class="uppercase text-2xl mb-2">
      {{ isRecollating ? 'Recollating...' : 'In Orbit...' }}
    </h2>
    <p class="mb-2 text-gray-100">
      <!-- eslint-disable-next-line -->
      <span v-if="
        executiveReport
        && executiveReport.initiatedBy
        && executiveReport.initiatedBy.email
      ">
        <span class="font-semibold text-white">
          {{ executiveReport.initiatedBy.email }}
        </span>
        will receive an email when the report is ready to view.
      </span>
    </p>
    <p v-if="isRecollating" class="mb-6 text-gray-100">
      This completed report is being reanalyzed with the latest rules and settings.
    </p>
    <p v-else class="mb-6 text-gray-100">
      Please be patient - orbits can run anywhere from 2 hours to 48 hours.
    </p>
    <ul v-if="isRecollating">
      <li class="flex items-center uppercase text-gray-200 mb-4">
        <span class="progress-spinner"></span>
        <span class="ml-2 font-semibold text-white">
          Reanalyzing completed report
          <span class="progress-status ml-2">
            [ In progress ]
          </span>
        </span>
      </li>
    </ul>
    <ul v-else>
      <li v-for="(stage, index) in stages"
          class="flex items-center uppercase text-gray-200 mb-4"
          :key="`step-${index}`">
        <transition name="fade" mode="in-out">
          <CheckIcon v-if="isCompleted(index)" :key="`step-${index}-check`" />
          <span v-else-if="isActive(index)"
                class="progress-spinner"
                :key="`step-${index}-spinner`"></span>
          <CircleIcon v-else :key="`step-${index}-circle`" />
        </transition>
        <span class="ml-2 transition-colors duration-150 ease-linear"
              :class="{
                'text-white': isCompleted(index) || isActive(index),
                'font-semibold': isActive(index),
              }">
          {{ stageLabel(stage, index) }}
          <span v-if="isActive(index)" class="progress-status ml-2">
            [ In progress ]
          </span>
        </span>
      </li>
    </ul>
    <div class="telemetry">
      <div class="telemetry-heading">
        <span class="telemetry-marker"></span>
        Live telemetry
      </div>
      <div class="telemetry-grid">
        <div v-for="metric in telemetryMetrics"
             :key="metric.label"
             class="telemetry-metric">
          <span class="telemetry-value">{{ metric.value }}</span>
          <span class="telemetry-label">{{ metric.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CircleIcon from '@/assets/circle.svg?inline';
import CheckIcon from '@/assets/circle_checked.svg?inline';

export default {
  name: 'ProgressReport',
  components: {
    CircleIcon,
    CheckIcon,
  },
  data() {
    return {
      stages: [
        {
          pending: 'Launch probes into orbit',
          active: 'Launching probes into orbit',
          completed: 'Probes launched into orbit',
        },
        {
          pending: 'Discover assets in perimeter',
          active: 'Discovering assets in perimeter',
          completed: 'Perimeter assets discovered',
        },
        {
          pending: 'Analyze discovered assets',
          active: 'Analyzing discovered assets',
          completed: 'Discovered assets analyzed',
        },
      ],
    };
  },
  methods: {
    isCompleted(index) {
      return this.step >= index;
    },
    isActive(index) {
      return this.step + 1 === index;
    },
    stageLabel(stage, index) {
      if (this.isCompleted(index)) return stage.completed;
      if (this.isActive(index)) return stage.active;
      return stage.pending;
    },
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'executiveReport',
    ]),
    telemetryMetrics() {
      const telemetry = (this.executiveReport && this.executiveReport.liveTelemetry) || {};
      return [
        { label: 'Hosts found', value: telemetry.hostsFound || 0 },
        { label: 'Open ports', value: telemetry.openPorts || 0 },
        { label: 'Screenshots', value: telemetry.screenshots || 0 },
        { label: 'Attack pathways', value: telemetry.attackPathways || 0 },
      ];
    },
  },
  props: {
    /*
      The index of which step has been completed.
     */
    step: {
      type: Number,
      required: true,
    },
    isRecollating: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  position: absolute;
  transition: opacity 150ms ease;
}
.fade-enter, .fade-leave-to {
  position: absolute;
  opacity: 0;
}

.fade-in {
  animation: enter 1000ms linear both;
  animation-delay: 1s;
}

.progress-spinner {
  width: 1rem;
  height: 1rem;
  flex: 0 0 1rem;
  border: 2px dotted #FF4A2B;
  border-right-color: transparent;
  border-radius: 50%;
  animation: progress-spin 1400ms linear infinite;
}

.progress-status {
  color: #FF4A2B;
  font-size: 0.65rem;
  letter-spacing: 0.08em;
}

.telemetry {
  margin-top: 2.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #444040;
}

.telemetry-heading {
  display: flex;
  align-items: center;
  margin-bottom: 0.85rem;
  color: #b5b1ae;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.telemetry-marker {
  width: 0.4rem;
  height: 0.4rem;
  margin-right: 0.5rem;
  border-radius: 50%;
  background: #FF4A2B;
  box-shadow: 0 0 6px #FF4A2B;
}

.telemetry-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.75rem;
}

.telemetry-metric {
  display: flex;
  min-width: 0;
  padding-top: 0.25rem;
  flex-direction: column;
}

.telemetry-metric::before {
  width: 1.35rem;
  height: 1px;
  margin-bottom: 0.65rem;
  background: #FF4A2B;
  box-shadow: 0 0 5px rgba(255, 74, 43, 0.55);
  content: '';
}

.telemetry-value {
  color: white;
  font-size: 2rem;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.telemetry-label {
  margin-top: 0.45rem;
  color: #8a8888;
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
}

@media (max-width: 767px) {
  .telemetry-grid {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 1.5rem;
  }
}

@keyframes progress-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes enter {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
